import React, { lazy, Suspense } from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import styles from "./Styles/App.module.css";
import { createBrowserRouter, RouterProvider, Navigate } from "react-router-dom";
import ProtectedRoute from "./components/ProtectedRoute";
import Spinner from "./components/Spinner";

// Auth pages (eagerly loaded since they're entry points)
import Login from "./Pages/auth/Login/Login";
import Register from "./Pages/auth/Register/Register";
import ForgotPassword from "./Pages/auth/Reset/Reset";

// Layout (eagerly loaded since it wraps all protected routes)
import Dashboard from "./Pages/dashboard/Dashboard";
import Settings from "./Pages/Settings/Settings";

// Lazy-loaded page components
const Overview = lazy(() => import("./Pages/overview/Overview"));
const Product = lazy(() => import("./Pages/Product/Product"));
const ProductForm = lazy(() => import("./Pages/Product/ProductForm"));
const Order = lazy(() => import("./Pages/order/Order"));

const Pin = lazy(() => import("./Pages/pin/Pin"));
const PinForm = lazy(() => import("./Pages/pin/PinForm"));
const PinHistory = lazy(() => import("./Pages/pin/SalesHistory"));
const MarkAsUsed = lazy(() => import("./Pages/pin/MarkAsUsed"));
const TypePrice = lazy(() => import("./Pages/pin/pintype/TypePrice"));
const TypeList = lazy(() => import("./Pages/pin/pintype/TypeList"));
const ExportPin = lazy(() => import("./Pages/pin/pinexport/ExportPin"));
const ExportList = lazy(() => import("./Pages/pin/pinexport/ExportList"));

const NewSalesHistory = lazy(() => import("./Pages/newpinsales/NewSalesHistory"));
const BoughtDistributor = lazy(() => import("./Pages/newpinsales/distributors/BoughtPins"));
const SoldDistributor = lazy(() => import("./Pages/newpinsales/distributors/SoldPins"));
const ExportedDistributor = lazy(() => import("./Pages/newpinsales/distributors/ExportedPins"));
const ExportDetailsD = lazy(() => import("./Pages/newpinsales/distributors/ExportDetails"));
const BoughtFixed = lazy(() => import("./Pages/newpinsales/fixedagents/BoughtPins"));
const SoldFixed = lazy(() => import("./Pages/newpinsales/fixedagents/SoldPins"));
const ExportedFixed = lazy(() => import("./Pages/newpinsales/fixedagents/ExportedPins"));
const ExportDetailsF = lazy(() => import("./Pages/newpinsales/fixedagents/ExportDetails"));

const Distributors = lazy(() => import("./Pages/Users/Distributors"));
const Admins = lazy(() => import("./Pages/Users/Admins"));
const FixedAgents = lazy(() => import("./Pages/Users/FixedAgents"));
const Customers = lazy(() => import("./Pages/Users/Customers"));
const NewUser = lazy(() => import("./Pages/Users/NewUser"));
const NewDistributor = lazy(() => import("./Pages/Users/NewDistributor"));

const History = lazy(() => import("./Pages/Wallets/Distributors/History"));
const DistributorDebit = lazy(() => import("./Pages/Wallets/Distributors/DistributorDebit"));
const FixedWallet = lazy(() => import("./Pages/Wallets/FixedAgents/Credit"));
const Debit = lazy(() => import("./Pages/Wallets/FixedAgents/Debit"));

const Cimport = lazy(() => import("./Pages/customers/Cimport"));
const Cexport = lazy(() => import("./Pages/customers/Cexport"));

const OrderHistory = lazy(() => import("./Pages/Mannual/OrderHistory"));
const OrderDetails = lazy(() => import("./Pages/Mannual/OrderDetails"));
const OrderTransaction = lazy(() => import("./Pages/Mannual/OrderTransaction"));

const BulkEmailSender = lazy(() => import("./components/Bulkmessages"));

function App() {
  const router = createBrowserRouter([
    {
      path: "/login",
      element: <Login />,
    },
    {
      path: "/registerhh",
      element: <Register />,
    },
    {
      path: "/reset-password",
      element: <ForgotPassword />,
    },
    {
      path: "/",
      element: <ProtectedRoute><Dashboard /></ProtectedRoute>,
      children: [
        {
          index: true,
          element: <Navigate to="/login" replace />,
        },
        {
          path: "/sms",
          element: <BulkEmailSender />,
        },
        {
          path: "/settings",
          element: <Settings />,
        },
        {
          path: "/overview",
          element: <Overview />,
        },
        {
          path: "/product/list",
          element: <Product />,
        },
        {
          path: "/product/create",
          element: <ProductForm />,
        },
        {
          path: "/order",
          element: <Order />,
        },
        {
          path: "/pin/list",
          element: <Pin />,
        },
        {
          path: "/pin/create",
          element: <PinForm />,
        },
        {
          path: "/pin/history",
          element: <PinHistory />,
        },
        {
          path: "/pin/mark-as-used",
          element: <MarkAsUsed />,
        },
        {
          path: "/pin/type/price",
          element: <TypePrice />,
        },
        {
          path: "/pin/type/list",
          element: <TypeList />,
        },
        {
          path: "/pin/export",
          element: <ExportPin />,
        },
        {
          path: "/pin/export/list",
          element: <ExportList />,
        },
        {
          path: "/pinsales/history",
          element: <NewSalesHistory />,
        },
        {
          path: "/pinsales/distributor/sold",
          element: <SoldDistributor />,
        },
        {
          path: "/pinsales/distributor/bought",
          element: <BoughtDistributor />,
        },
        {
          path: "/pinsales/distributor/exported",
          element: <ExportedDistributor />,
        },
        {
          path: "/pinsales/fixedagent/sold",
          element: <SoldFixed />,
        },
        {
          path: "/pinsales/fixedagent/bought",
          element: <BoughtFixed />,
        },
        {
          path: "/pinsales/fixedagent/exported",
          element: <ExportedFixed />,
        },
        {
          path: "/pinsales/distributor/exported/:id",
          element: <ExportDetailsD />,
        },
        {
          path: "/pinsales/fixedagent/exported/:id",
          element: <ExportDetailsF />,
        },
        {
          path: "/users/distributors",
          element: <Distributors />,
        },
        {
          path: "/users/admins",
          element: <Admins />,
        },
        {
          path: "/users/fixedagents",
          element: <FixedAgents />,
        },
        {
          path: "/users/customers",
          element: <Customers />,
        },
        {
          path: "/users/create",
          element: <NewUser />,
        },
        {
          path: "/users/create/distributor",
          element: <NewDistributor />,
        },
        {
          path: "wallet/distributor/history",
          element: <History />,
        },
        {
          path: "wallet/distributor/debit",
          element: <DistributorDebit />,
        },
        {
          path: "wallet/fixedagent/credit",
          element: <FixedWallet />,
        },
        {
          path: "wallet/fixedagent/debit",
          element: <Debit />,
        },
        {
          path: "customers/import",
          element: <Cimport />,
        },
        {
          path: "customers/export",
          element: <Cexport />,
        },
        {
          path: "manual/history",
          element: <OrderHistory />,
        },
        {
          path: "/manual/history/manual/details/:id",
          element: <OrderDetails />,
        },
        {
          path: "manual/Transactions",
          element: <OrderTransaction />,
        },
      ],
    },
  ]);
  return (
    <div className={styles.app}>
      <Suspense fallback={null}>
        <RouterProvider router={router} />
      </Suspense>
      <ToastContainer />
    </div>
  );
}

export default App;
